import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { LoadZoneObjectName } from "./LoadZoneActualLoadVisual";
import {
  getLoadZoneMaxItems,
  matchesSelectedIso,
  normalizeObjectName,
  pickTopLoadZoneObjects,
} from "./loadZoneUtils";
import { MARKET_MONITOR_TOOLTIP_PROPS } from "./tooltipConfig";

interface MarketDataRow {
  ISO?: string;
  OBJECTNAME?: string | null;
  DATATYPEID?: number | string | null;
  TIMESTAMP?: string;
  DATETIME?: string;
  SHORTNAME?: string;
  VALUE?: string | number | null;
  VOLUME?: string | number | null;
}

interface DatatypeRow {
  DATATYPEID?: number | string | null;
  GENERATIONTYPE?: string | null;
}

interface LoadZoneRenewableSummaryVisualsProps {
  renewableForecastRows: MarketDataRow[];
  renewableRealtimeRows: MarketDataRow[];
  datatypesRows: DatatypeRow[];
  selectedIso: string;
  selectedObjectName: LoadZoneObjectName | null;
}

interface AggregatedRenewablePoint {
  index: number;
  timestampIso: string;
  timestampLabel: string;
  dateLabel: string;
  renewableGenRealTime: number | null;
  renewableGenForecast: number | null;
}

interface SolarWindSpreadPoint {
  index: number;
  timestampIso: string;
  timestampLabel: string;
  dateLabel: string;
  solarSpread: number;
  windSpread: number;
}

const MAX_POINTS = 14 * 24;
const EXCLUDED_GENERATION_TYPES = new Set(["BIO", "HYBRID"]);

const toNumber = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const toDatatypeKey = (value: number | string | null | undefined): string | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return String(value);
};

const matchesScopedLoadZoneObject = (
  row: MarketDataRow,
  scopedObjectNames: Set<string>
): boolean => {
  const objectName = normalizeObjectName(row.OBJECTNAME);
  if (!objectName) {
    return false;
  }

  return scopedObjectNames.has(objectName);
};

const hasAllowedGenerationType = (
  row: MarketDataRow,
  generationTypeByDatatype: Map<string, string>
): boolean => {
  const datatypeKey = toDatatypeKey(row.DATATYPEID);
  if (!datatypeKey) {
    return false;
  }

  const generationType = generationTypeByDatatype.get(datatypeKey)?.trim();
  if (!generationType) {
    return false;
  }

  return !EXCLUDED_GENERATION_TYPES.has(generationType.toUpperCase());
};

const getGenerationType = (
  row: MarketDataRow,
  generationTypeByDatatype: Map<string, string>
): string => {
  const datatypeKey = toDatatypeKey(row.DATATYPEID);
  if (!datatypeKey) {
    return "";
  }

  return generationTypeByDatatype.get(datatypeKey)?.trim().toUpperCase() ?? "";
};

const isSolarOrWindGeneration = (
  row: MarketDataRow,
  generationTypeByDatatype: Map<string, string>
): boolean => {
  const generationType = getGenerationType(row, generationTypeByDatatype);
  return generationType.includes("SOLAR") || generationType.includes("WIND");
};

const toHourKey = (value: string | undefined): string | null => {
  if (!value) {
    return null;
  }

  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const parsed = new Date(normalized);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  parsed.setMinutes(0, 0, 0);
  return parsed.toISOString();
};

const toShortDate = (isoDateTime: string): string => {
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) {
    return isoDateTime;
  }

  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
  });
};

const toFullTimeLabel = (isoDateTime: string): string => {
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) {
    return isoDateTime;
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const roundValue = (value: number): number => Math.round(value * 100) / 100;

const formatYAxis = (value: number): string => `${Math.round(value / 1000)}K`;

const formatTooltip = (value: number): string =>
  `${value.toLocaleString("en-US", { maximumFractionDigits: 0 })} MW`;

const formatSpreadTooltip = (value: number): string =>
  `${value.toLocaleString("en-US", { maximumFractionDigits: 2 })} MW`;

const buildAggregatedRenewableData = (
  renewableForecastRowsAll: MarketDataRow[],
  renewableRealtimeRowsAll: MarketDataRow[]
): AggregatedRenewablePoint[] => {
  const renewableForecastAllByHour = new Map<string, number>();
  const renewableRealtimeAllByHour = new Map<string, number>();

  renewableForecastRowsAll.forEach((row) => {
    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VOLUME ?? row.VALUE);
    if (!hourKey || value === null) {
      return;
    }

    renewableForecastAllByHour.set(hourKey, (renewableForecastAllByHour.get(hourKey) ?? 0) + value);
  });

  renewableRealtimeRowsAll.forEach((row) => {
    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VOLUME ?? row.VALUE);
    if (!hourKey || value === null) {
      return;
    }

    renewableRealtimeAllByHour.set(hourKey, (renewableRealtimeAllByHour.get(hourKey) ?? 0) + value);
  });

  const forecastTimeline = Array.from(renewableForecastAllByHour.keys()).sort();
  const combinedTimeline = Array.from(
    new Set([...renewableForecastAllByHour.keys(), ...renewableRealtimeAllByHour.keys()])
  ).sort();

  // Keep load-zone date window aligned to forecast timestamps (Power BI parity).
  const timeline = (forecastTimeline.length > 0 ? forecastTimeline : combinedTimeline).slice(
    -MAX_POINTS
  );

  return timeline.map((hourKey, index) => ({
    index,
    timestampIso: hourKey,
    timestampLabel: toFullTimeLabel(hourKey),
    dateLabel: toShortDate(hourKey),
    renewableGenRealTime:
      renewableRealtimeAllByHour.has(hourKey)
        ? roundValue(renewableRealtimeAllByHour.get(hourKey) ?? 0)
        : null,
    renewableGenForecast:
      renewableForecastAllByHour.has(hourKey)
        ? roundValue(renewableForecastAllByHour.get(hourKey) ?? 0)
        : null,
  }));
};

const buildSolarWindSpreadData = (
  renewableForecastRows: MarketDataRow[],
  renewableRealtimeRows: MarketDataRow[],
  generationTypeByDatatype: Map<string, string>
): SolarWindSpreadPoint[] => {
  const solarForecastByHour = new Map<string, number>();
  const windForecastByHour = new Map<string, number>();
  const solarRealtimeByHour = new Map<string, number>();
  const windRealtimeByHour = new Map<string, number>();

  renewableForecastRows.forEach((row) => {
    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VOLUME ?? row.VALUE);
    if (!hourKey || value === null) {
      return;
    }

    const generationType = getGenerationType(row, generationTypeByDatatype);
    if (generationType.includes("SOLAR")) {
      solarForecastByHour.set(hourKey, (solarForecastByHour.get(hourKey) ?? 0) + value);
      return;
    }

    if (generationType.includes("WIND")) {
      windForecastByHour.set(hourKey, (windForecastByHour.get(hourKey) ?? 0) + value);
    }
  });

  renewableRealtimeRows.forEach((row) => {
    const hourKey = toHourKey(row.TIMESTAMP ?? row.DATETIME);
    const value = toNumber(row.VOLUME ?? row.VALUE);
    if (!hourKey || value === null) {
      return;
    }

    const generationType = getGenerationType(row, generationTypeByDatatype);
    if (generationType.includes("SOLAR")) {
      solarRealtimeByHour.set(hourKey, (solarRealtimeByHour.get(hourKey) ?? 0) + value);
      return;
    }

    if (generationType.includes("WIND")) {
      windRealtimeByHour.set(hourKey, (windRealtimeByHour.get(hourKey) ?? 0) + value);
    }
  });

  const forecastTimeline = Array.from(
    new Set([...solarForecastByHour.keys(), ...windForecastByHour.keys()])
  ).sort();
  const combinedTimeline = Array.from(
    new Set([
      ...solarForecastByHour.keys(),
      ...windForecastByHour.keys(),
      ...solarRealtimeByHour.keys(),
      ...windRealtimeByHour.keys(),
    ])
  ).sort();

  // Keep load-zone date window aligned to forecast timestamps (Power BI parity).
  const timeline = (forecastTimeline.length > 0 ? forecastTimeline : combinedTimeline).slice(
    -MAX_POINTS
  );

  return timeline.map((hourKey, index) => {
    const solarActual = solarRealtimeByHour.get(hourKey) ?? null;
    const solarForecast = solarForecastByHour.get(hourKey) ?? 0;
    const windActual = windRealtimeByHour.get(hourKey) ?? null;
    const windForecast = windForecastByHour.get(hourKey) ?? 0;

    return {
      index,
      timestampIso: hourKey,
      timestampLabel: toFullTimeLabel(hourKey),
      dateLabel: toShortDate(hourKey),
      solarSpread: solarActual !== null ? roundValue(solarActual - solarForecast) : 0,
      windSpread: windActual !== null ? roundValue(windActual - windForecast) : 0,
    };
  });
};

const LoadZoneRenewableSummaryVisuals: React.FC<LoadZoneRenewableSummaryVisualsProps> = ({
  renewableForecastRows,
  renewableRealtimeRows,
  datatypesRows,
  selectedIso,
  selectedObjectName,
}) => {
  const generationTypeByDatatype = useMemo(() => {
    const map = new Map<string, string>();
    datatypesRows.forEach((row) => {
      const datatypeKey = toDatatypeKey(row.DATATYPEID);
      const generationType = row.GENERATIONTYPE?.trim();

      if (datatypeKey && generationType) {
        map.set(datatypeKey, generationType);
      }
    });

    return map;
  }, [datatypesRows]);

  const scopedObjectNames = useMemo(() => {
    if (selectedObjectName) {
      return new Set([selectedObjectName.toUpperCase()]);
    }

    const objectTotals = new Map<string, number>();

    const collect = (rows: MarketDataRow[]) => {
      rows.forEach((row) => {
        if (!matchesSelectedIso(row.ISO, selectedIso)) {
          return;
        }

        if (!hasAllowedGenerationType(row, generationTypeByDatatype)) {
          return;
        }

        const objectName = normalizeObjectName(row.OBJECTNAME);
        if (!objectName) {
          return;
        }

        const value = toNumber(row.VOLUME ?? row.VALUE);
        if (value === null) {
          return;
        }

        objectTotals.set(objectName, (objectTotals.get(objectName) ?? 0) + Math.abs(value));
      });
    };

    collect(renewableForecastRows);
    collect(renewableRealtimeRows);

    const topObjects = pickTopLoadZoneObjects(
      objectTotals,
      selectedIso,
      getLoadZoneMaxItems(selectedIso)
    );

    return new Set(topObjects);
  }, [
    generationTypeByDatatype,
    renewableForecastRows,
    renewableRealtimeRows,
    selectedIso,
    selectedObjectName,
  ]);

  const renewableForecastRowsAll = useMemo(
    () =>
      renewableForecastRows.filter(
        (row) =>
          matchesSelectedIso(row.ISO, selectedIso) &&
          matchesScopedLoadZoneObject(row, scopedObjectNames) &&
          isSolarOrWindGeneration(row, generationTypeByDatatype)
      ),
    [renewableForecastRows, selectedIso, scopedObjectNames, generationTypeByDatatype]
  );

  const renewableRealtimeRowsAll = useMemo(
    () =>
      renewableRealtimeRows.filter(
        (row) =>
          matchesSelectedIso(row.ISO, selectedIso) &&
          matchesScopedLoadZoneObject(row, scopedObjectNames) &&
          isSolarOrWindGeneration(row, generationTypeByDatatype)
      ),
    [renewableRealtimeRows, selectedIso, scopedObjectNames, generationTypeByDatatype]
  );

  const renewableForecastRowsForSpread = useMemo(
    () =>
      renewableForecastRows.filter(
        (row) =>
          matchesSelectedIso(row.ISO, selectedIso) &&
          matchesScopedLoadZoneObject(row, scopedObjectNames) &&
          hasAllowedGenerationType(row, generationTypeByDatatype)
      ),
    [renewableForecastRows, generationTypeByDatatype, selectedIso, scopedObjectNames]
  );

  const renewableRealtimeRowsForSpread = useMemo(
    () =>
      renewableRealtimeRows.filter(
        (row) =>
          matchesSelectedIso(row.ISO, selectedIso) &&
          matchesScopedLoadZoneObject(row, scopedObjectNames) &&
          hasAllowedGenerationType(row, generationTypeByDatatype)
      ),
    [renewableRealtimeRows, generationTypeByDatatype, selectedIso, scopedObjectNames]
  );

  const selectedObjectPrefix = selectedObjectName ? `${selectedObjectName} ` : "";

  const aggregatedData = useMemo<AggregatedRenewablePoint[]>(
    () => buildAggregatedRenewableData(renewableForecastRowsAll, renewableRealtimeRowsAll),
    [renewableForecastRowsAll, renewableRealtimeRowsAll]
  );

  const solarWindSpreadData = useMemo<SolarWindSpreadPoint[]>(
    () =>
      buildSolarWindSpreadData(
        renewableForecastRowsForSpread,
        renewableRealtimeRowsForSpread,
        generationTypeByDatatype
      ),
    [renewableForecastRowsForSpread, renewableRealtimeRowsForSpread, generationTypeByDatatype]
  );

  const xTicksAggregated = useMemo(
    () =>
      aggregatedData
        .filter((item) => item.index % 48 === 0 || item.index === aggregatedData.length - 1)
        .map((item) => item.index),
    [aggregatedData]
  );

  const xTicksSpread = useMemo(
    () =>
      solarWindSpreadData
        .filter((item) => item.index % 48 === 0 || item.index === solarWindSpreadData.length - 1)
        .map((item) => item.index),
    [solarWindSpreadData]
  );

  return (
    <div className="space-y-5 xl:space-y-0 xl:flex xl:flex-col xl:gap-5 xl:h-full">
      <div className="w-full h-[360px] xl:h-auto xl:flex-1 rounded-xl border border-gray-200 bg-white p-4">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          {selectedObjectPrefix}Aggregated Solar &amp; Wind Generation &amp; Forecast
        </h3>
        <ResponsiveContainer width="100%" height="88%">
          <LineChart data={aggregatedData} margin={{ top: 8, right: 20, left: 10, bottom: 8 }}>
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
            <XAxis
              dataKey="index"
              tickFormatter={(value) => aggregatedData[value]?.dateLabel ?? ""}
              ticks={xTicksAggregated}
              tick={{ fill: "#6b7280", fontSize: 12 }}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fill: "#6b7280", fontSize: 12 }}
              label={{ value: "MW", angle: -90, position: "insideLeft", offset: 0 }}
            />
            <Tooltip
              {...MARKET_MONITOR_TOOLTIP_PROPS}
              formatter={(value: number) => formatTooltip(value)}
              labelFormatter={(value) => aggregatedData[value]?.timestampLabel ?? ""}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="renewableGenRealTime"
              name="Renewable Gen Real Time"
              stroke="#1e88e5"
              strokeWidth={2.6}
              dot={false}
            />
            <Line
              type="monotone"
              dataKey="renewableGenForecast"
              name="Renewable_Gen_Forecast"
              stroke="#1f2a99"
              strokeWidth={2.6}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="w-full h-[360px] xl:h-auto xl:flex-1 rounded-xl border border-gray-200 bg-white p-4">
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          {selectedObjectPrefix}Solar &amp; Wind Actual vs Forecast Spread
        </h3>
        <ResponsiveContainer width="100%" height="88%">
          <BarChart data={solarWindSpreadData} margin={{ top: 8, right: 20, left: 10, bottom: 8 }}>
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
            <XAxis
              dataKey="index"
              tickFormatter={(value) => solarWindSpreadData[value]?.dateLabel ?? ""}
              ticks={xTicksSpread}
              tick={{ fill: "#6b7280", fontSize: 12 }}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fill: "#6b7280", fontSize: 12 }}
              label={{ value: "MW", angle: -90, position: "insideLeft", offset: 0 }}
            />
            <ReferenceLine y={0} stroke="#9ca3af" />
            <Tooltip
              {...MARKET_MONITOR_TOOLTIP_PROPS}
              formatter={(value: number) => formatSpreadTooltip(value)}
              labelFormatter={(value) => solarWindSpreadData[value]?.timestampLabel ?? ""}
            />
            <Legend />
            <Bar dataKey="solarSpread" name="Solar" fill="#1e88e5" barSize={3} />
            <Bar dataKey="windSpread" name="Wind" fill="#1f2a99" barSize={3} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LoadZoneRenewableSummaryVisuals;
